import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  NotificationsActive as LaunchIcon,
  PauseCircle as PauseIcon,
  PlayArrow as PlayIcon,
  StopCircle as StopIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAdminCircle,
  createWarmupSignalRule,
  deleteWarmupSignalRule,
  getAdminCircles,
  getAdminPairs,
  getWarmupSignalRules,
  runWarmupSignalRuleNow,
  stopAdminCircle,
  updateWarmupSignalRule,
  updateWarmupSignalRuleStatus,
} from '@/services/api';
import { SectionHeader } from '@/components/common/Page';
import { useAdminI18n } from '@/contexts/AdminI18nContext';
import { useAdminTime } from '@/contexts/AdminTimeContext';
import type { AdminCircle, ArbitragePair, WarmupSignalRule, WarmupSignalRuleInput, WarmupSignalRun } from '@/types';
import {
  isAllowedDecimalInput,
  isAllowedIntegerInput,
  parseNumericInput,
  type NumericInputValue,
} from '@/utils/numberInput';

const DEFAULT_SIGNAL_SPREAD_PERCENT = 0.5;
const DEFAULT_NEGATIVE_SPREAD_PERCENT = -0.2;
const DEFAULT_COOLDOWN_HOURS = 12;
const DEFAULT_WARMUP_INTERVAL_MIN_MINUTES = 100;
const DEFAULT_WARMUP_INTERVAL_MAX_MINUTES = 200;
const DEFAULT_WARMUP_DURATION_MIN_MINUTES = 30;
const DEFAULT_WARMUP_DURATION_MAX_MINUTES = 60;
const DEFAULT_WARMUP_SPREAD_MIN_PERCENT = 2;
const DEFAULT_WARMUP_SPREAD_MAX_PERCENT = 3;
const WARMUP_MANUAL_ONLY_COINS = new Set(['HIVE', 'STEEM']);

type SignalsTab = 'manual' | 'warmup';

type CircleFormData = {
  pair_id: number | '';
  signal_spread_percent: NumericInputValue;
  negative_spread_percent: NumericInputValue;
  cooldown_hours: NumericInputValue;
  expires_in_hours: NumericInputValue;
  notes: string;
};

type WarmupFormData = {
  id: number | null;
  name: string;
  status: 'active' | 'paused';
  pair_ids: number[];
  interval_min_minutes: NumericInputValue;
  interval_max_minutes: NumericInputValue;
  duration_min_minutes: NumericInputValue;
  duration_max_minutes: NumericInputValue;
  spread_min_percent: NumericInputValue;
  spread_max_percent: NumericInputValue;
  negative_spread_percent: NumericInputValue;
  cooldown_hours: NumericInputValue;
  notes: string;
};

type PairLabelSource = {
  pair_type?: 'single' | 'double' | null;
  coin_from?: string | null;
  coin_to?: string | null;
  exchange_buy_name?: string | null;
  exchange_sell_name?: string | null;
  exchange_buy?: string | null;
  exchange_sell?: string | null;
};

const decimalInputProps = {
  inputMode: 'decimal' as const,
};

const integerInputProps = {
  inputMode: 'numeric' as const,
};

function createDefaultWarmupForm(): WarmupFormData {
  return {
    id: null,
    name: 'Warmup schedule',
    status: 'active',
    pair_ids: [],
    interval_min_minutes: DEFAULT_WARMUP_INTERVAL_MIN_MINUTES,
    interval_max_minutes: DEFAULT_WARMUP_INTERVAL_MAX_MINUTES,
    duration_min_minutes: DEFAULT_WARMUP_DURATION_MIN_MINUTES,
    duration_max_minutes: DEFAULT_WARMUP_DURATION_MAX_MINUTES,
    spread_min_percent: DEFAULT_WARMUP_SPREAD_MIN_PERCENT,
    spread_max_percent: DEFAULT_WARMUP_SPREAD_MAX_PERCENT,
    negative_spread_percent: DEFAULT_NEGATIVE_SPREAD_PERCENT,
    cooldown_hours: DEFAULT_COOLDOWN_HOURS,
    notes: '',
  };
}

function pairLabel(pair?: PairLabelSource | null) {
  if (!pair) return '';
  const coinFrom = pair.coin_from || '';
  const coins = pair.pair_type === 'double' && pair.coin_to ? `${coinFrom} -> ${pair.coin_to}` : coinFrom;
  const buy = pair.exchange_buy_name || pair.exchange_buy || '';
  const sell = pair.exchange_sell_name || pair.exchange_sell || '';
  return `${coins || '-'} | ${buy || '-'} -> ${sell || '-'}`;
}

function isWarmupEligiblePair(pair: PairLabelSource) {
  return !WARMUP_MANUAL_ONLY_COINS.has(pair.coin_from?.toUpperCase() || '')
    && !WARMUP_MANUAL_ONLY_COINS.has(pair.coin_to?.toUpperCase() || '');
}

function formatRange(min: number | null | undefined, max: number | null | undefined, suffix: string) {
  const minText = min ?? '-';
  const maxText = max ?? '-';
  return min === max ? `${minText}${suffix}` : `${minText}-${maxText}${suffix}`;
}

function isCircleActive(circle: Pick<AdminCircle, 'status' | 'expires_at'>) {
  if (circle.status !== 'active' || !circle.expires_at) return circle.status === 'active';

  const expiresAt = Date.parse(circle.expires_at);
  return Number.isNaN(expiresAt) || expiresAt > Date.now();
}

function getRulePairIds(rule: WarmupSignalRule) {
  if (Array.isArray(rule.pair_ids_list)) return rule.pair_ids_list;

  try {
    const parsed = JSON.parse(rule.pair_ids);
    return Array.isArray(parsed)
      ? parsed.map((id) => Number(id)).filter((id) => Number.isFinite(id))
      : [];
  } catch {
    return rule.pair_ids
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id));
  }
}

function buildWarmupPayload(formData: WarmupFormData): WarmupSignalRuleInput {
  return {
    name: formData.name.trim() || undefined,
    status: formData.status,
    pair_ids: formData.pair_ids,
    interval_min_minutes: parseNumericInput(formData.interval_min_minutes) ?? DEFAULT_WARMUP_INTERVAL_MIN_MINUTES,
    interval_max_minutes: parseNumericInput(formData.interval_max_minutes) ?? DEFAULT_WARMUP_INTERVAL_MAX_MINUTES,
    duration_min_minutes: parseNumericInput(formData.duration_min_minutes) ?? DEFAULT_WARMUP_DURATION_MIN_MINUTES,
    duration_max_minutes: parseNumericInput(formData.duration_max_minutes) ?? DEFAULT_WARMUP_DURATION_MAX_MINUTES,
    spread_min_percent: parseNumericInput(formData.spread_min_percent) ?? DEFAULT_WARMUP_SPREAD_MIN_PERCENT,
    spread_max_percent: parseNumericInput(formData.spread_max_percent) ?? DEFAULT_WARMUP_SPREAD_MAX_PERCENT,
    negative_spread_percent: parseNumericInput(formData.negative_spread_percent) ?? DEFAULT_NEGATIVE_SPREAD_PERCENT,
    cooldown_hours: parseNumericInput(formData.cooldown_hours) ?? DEFAULT_COOLDOWN_HOURS,
    notes: formData.notes.trim() || null,
  };
}

export default function AdminCircles() {
  const queryClient = useQueryClient();
  const { t } = useAdminI18n();
  const { formatDateTime } = useAdminTime();
  const [activeTab, setActiveTab] = useState<SignalsTab>('manual');
  const [formData, setFormData] = useState<CircleFormData>({
    pair_id: '',
    signal_spread_percent: DEFAULT_SIGNAL_SPREAD_PERCENT,
    negative_spread_percent: DEFAULT_NEGATIVE_SPREAD_PERCENT,
    cooldown_hours: DEFAULT_COOLDOWN_HOURS,
    expires_in_hours: '',
    notes: '',
  });
  const [warmupFormData, setWarmupFormData] = useState<WarmupFormData>(createDefaultWarmupForm);

  const { data: pairsData, isLoading: pairsLoading } = useQuery({
    queryKey: ['admin-pairs'],
    queryFn: getAdminPairs,
  });

  const { data: circlesData, isLoading: circlesLoading, error } = useQuery({
    queryKey: ['admin-circles'],
    queryFn: () => getAdminCircles(),
    refetchInterval: 30_000,
  });

  const { data: warmupsData, isLoading: warmupsLoading, error: warmupsError } = useQuery({
    queryKey: ['admin-signal-warmups'],
    queryFn: getWarmupSignalRules,
  });

  const pairs = pairsData?.pairs ?? [];
  const circles = circlesData?.circles ?? [];
  const warmupRules = warmupsData?.rules ?? [];
  const warmupRuns = warmupsData?.runs ?? [];
  const selectedPair = useMemo(
    () => pairs.find((pair) => pair.id === formData.pair_id),
    [formData.pair_id, pairs],
  );
  const pairLookup = useMemo(
    () => new Map(pairs.map((pair) => [pair.id, pair])),
    [pairs],
  );
  const warmupEligiblePairs = useMemo(
    () => pairs.filter(isWarmupEligiblePair),
    [pairs],
  );
  const warmupEligiblePairIds = useMemo(
    () => new Set(warmupEligiblePairs.map((pair) => pair.id)),
    [warmupEligiblePairs],
  );
  const activeCircleCount = circles.filter(isCircleActive).length;

  useEffect(() => {
    if (!selectedPair) return;
    setFormData((current) => ({
      ...current,
      signal_spread_percent:
        parseNumericInput(current.signal_spread_percent)
        ?? selectedPair.base_spread_percent
        ?? DEFAULT_SIGNAL_SPREAD_PERCENT,
      negative_spread_percent: selectedPair.negative_spread_percent ?? DEFAULT_NEGATIVE_SPREAD_PERCENT,
    }));
  }, [selectedPair]);

  useEffect(() => {
    setWarmupFormData((current) => {
      const pairIds = current.pair_ids.filter((pairId) => warmupEligiblePairIds.has(pairId));
      return pairIds.length === current.pair_ids.length ? current : { ...current, pair_ids: pairIds };
    });
  }, [warmupEligiblePairIds]);

  const invalidateSignalQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-circles'] });
    queryClient.invalidateQueries({ queryKey: ['admin-signal-warmups'] });
    queryClient.invalidateQueries({ queryKey: ['pairs'] });
    queryClient.invalidateQueries({ queryKey: ['admin-pairs'] });
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const expiresInHours = parseNumericInput(formData.expires_in_hours);
      const expiresAt = expiresInHours && expiresInHours > 0
        ? new Date(Date.now() + expiresInHours * 60 * 60 * 1000).toISOString()
        : null;
      const signalSpreadPercent = parseNumericInput(formData.signal_spread_percent) ?? DEFAULT_SIGNAL_SPREAD_PERCENT;
      const negativeSpreadPercent = parseNumericInput(formData.negative_spread_percent) ?? DEFAULT_NEGATIVE_SPREAD_PERCENT;
      const cooldownHours = parseNumericInput(formData.cooldown_hours) ?? DEFAULT_COOLDOWN_HOURS;

      return createAdminCircle({
        pair_id: Number(formData.pair_id),
        signal_spread_percent: signalSpreadPercent,
        negative_spread_percent: negativeSpreadPercent,
        cooldown_hours: cooldownHours,
        expires_at: expiresAt,
        notes: formData.notes.trim() || undefined,
      });
    },
    onSuccess: () => {
      invalidateSignalQueries();
      setFormData((current) => ({ ...current, notes: '' }));
    },
  });

  const stopMutation = useMutation({
    mutationFn: stopAdminCircle,
    onSuccess: invalidateSignalQueries,
  });

  const saveWarmupMutation = useMutation({
    mutationFn: () => {
      const payload = buildWarmupPayload({
        ...warmupFormData,
        pair_ids: warmupFormData.pair_ids.filter((pairId) => warmupEligiblePairIds.has(pairId)),
      });
      return warmupFormData.id
        ? updateWarmupSignalRule(warmupFormData.id, payload)
        : createWarmupSignalRule(payload);
    },
    onSuccess: () => {
      invalidateSignalQueries();
      setWarmupFormData(createDefaultWarmupForm());
    },
  });

  const updateWarmupStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'paused' }) =>
      updateWarmupSignalRuleStatus(id, status),
    onSuccess: invalidateSignalQueries,
  });

  const runWarmupNowMutation = useMutation({
    mutationFn: runWarmupSignalRuleNow,
    onSuccess: invalidateSignalQueries,
  });

  const deleteWarmupMutation = useMutation({
    mutationFn: deleteWarmupSignalRule,
    onSuccess: () => {
      invalidateSignalQueries();
      setWarmupFormData((current) => (
        current.id ? createDefaultWarmupForm() : current
      ));
    },
  });

  const hasValidCircleNumbers =
    parseNumericInput(formData.signal_spread_percent) !== null
    && parseNumericInput(formData.negative_spread_percent) !== null
    && parseNumericInput(formData.cooldown_hours) !== null;

  const warmupIntervalMin = parseNumericInput(warmupFormData.interval_min_minutes);
  const warmupIntervalMax = parseNumericInput(warmupFormData.interval_max_minutes);
  const warmupDurationMin = parseNumericInput(warmupFormData.duration_min_minutes);
  const warmupDurationMax = parseNumericInput(warmupFormData.duration_max_minutes);
  const warmupSpreadMin = parseNumericInput(warmupFormData.spread_min_percent);
  const warmupSpreadMax = parseNumericInput(warmupFormData.spread_max_percent);
  const warmupNegativeSpread = parseNumericInput(warmupFormData.negative_spread_percent);
  const warmupCooldownHours = parseNumericInput(warmupFormData.cooldown_hours);
  const hasValidWarmupNumbers =
    warmupFormData.pair_ids.length > 0
    && warmupIntervalMin !== null
    && warmupIntervalMax !== null
    && warmupIntervalMin > 0
    && warmupIntervalMax >= warmupIntervalMin
    && warmupDurationMin !== null
    && warmupDurationMax !== null
    && warmupDurationMin > 0
    && warmupDurationMax >= warmupDurationMin
    && warmupSpreadMin !== null
    && warmupSpreadMax !== null
    && warmupSpreadMin > 0
    && warmupSpreadMax >= warmupSpreadMin
    && warmupNegativeSpread !== null
    && warmupCooldownHours !== null
    && warmupCooldownHours > 0;

  const editWarmupRule = (rule: WarmupSignalRule) => {
    setWarmupFormData({
      id: rule.id,
      name: rule.name,
      status: rule.status,
      pair_ids: getRulePairIds(rule).filter((pairId) => warmupEligiblePairIds.has(pairId)),
      interval_min_minutes: rule.interval_min_minutes,
      interval_max_minutes: rule.interval_max_minutes,
      duration_min_minutes: rule.duration_min_minutes,
      duration_max_minutes: rule.duration_max_minutes,
      spread_min_percent: rule.spread_min_percent,
      spread_max_percent: rule.spread_max_percent,
      negative_spread_percent: rule.negative_spread_percent ?? DEFAULT_NEGATIVE_SPREAD_PERCENT,
      cooldown_hours: rule.cooldown_hours,
      notes: rule.notes ?? '',
    });
    setActiveTab('warmup');
  };

  const deleteWarmupRule = (rule: WarmupSignalRule) => {
    if (!window.confirm(t('admin.warmups.deleteConfirm', { name: rule.name }))) return;
    deleteWarmupMutation.mutate(rule.id);
  };

  const formatOptionalDate = (value: string | null | undefined) => (
    value ? formatDateTime(value) : '-'
  );

  if (pairsLoading || circlesLoading || warmupsLoading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <SectionHeader
        title={t('admin.circles.title')}
        detail={t('admin.circles.detail', { active: activeCircleCount, total: circles.length })}
      />

      <Tabs
        value={activeTab}
        onChange={(_, value: SignalsTab) => setActiveTab(value)}
        sx={{ mb: 2, minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}
      >
        <Tab value="manual" label={t('admin.circles.manualTab')} />
        <Tab value="warmup" label={t('admin.circles.warmupTab')} />
      </Tabs>

      {activeTab === 'manual' ? (
        <>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{t('admin.circles.loadError')}</Alert>}
          {createMutation.error && <Alert severity="error" sx={{ mb: 2 }}>{t('admin.circles.launchError')}</Alert>}

          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              border: '1px solid rgba(226, 255, 238, 0.12)',
              bgcolor: 'rgba(244, 255, 249, 0.045)',
            }}
          >
            <Stack spacing={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('admin.circles.pair')}</InputLabel>
                <Select
                  value={formData.pair_id}
                  label={t('admin.circles.pair')}
                  onChange={(event) => setFormData({ ...formData, pair_id: Number(event.target.value) })}
                >
                  {pairs.map((pair) => (
                    <MenuItem key={pair.id} value={pair.id}>
                      {pairLabel(pair)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 1.25 }}>
                <TextField
                  size="small"
                  label={t('admin.circles.signalSpread')}
                  inputProps={decimalInputProps}
                  value={formData.signal_spread_percent}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedDecimalInput(value)) {
                      setFormData({ ...formData, signal_spread_percent: value });
                    }
                  }}
                />
                <TextField
                  size="small"
                  label={t('admin.circles.negativeSpread')}
                  inputProps={decimalInputProps}
                  value={formData.negative_spread_percent}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedDecimalInput(value)) {
                      setFormData({ ...formData, negative_spread_percent: value });
                    }
                  }}
                />
                <TextField
                  size="small"
                  label={t('admin.circles.cooldownHours')}
                  inputProps={integerInputProps}
                  value={formData.cooldown_hours}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedIntegerInput(value)) {
                      setFormData({ ...formData, cooldown_hours: value });
                    }
                  }}
                />
                <TextField
                  size="small"
                  label={t('admin.circles.expiresIn')}
                  inputProps={integerInputProps}
                  value={formData.expires_in_hours}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedIntegerInput(value)) {
                      setFormData({ ...formData, expires_in_hours: value });
                    }
                  }}
                />
              </Box>

              <TextField
                size="small"
                label={t('admin.circles.notes')}
                value={formData.notes}
                onChange={(event) => setFormData({ ...formData, notes: event.target.value })}
                multiline
                rows={2}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  {selectedPair ? pairLabel(selectedPair) : t('admin.circles.noPairSelected')}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<LaunchIcon />}
                  onClick={() => createMutation.mutate()}
                  disabled={!formData.pair_id || !hasValidCircleNumbers || createMutation.isPending}
                >
                  {t('admin.circles.launch')}
                </Button>
              </Stack>
            </Stack>
          </Box>

          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.common.status')}</TableCell>
                  <TableCell>{t('admin.circles.pair')}</TableCell>
                  <TableCell>{t('admin.circles.signalSpreadShort')}</TableCell>
                  <TableCell>{t('admin.circles.cooldownShort')}</TableCell>
                  <TableCell>{t('admin.circles.notifications')}</TableCell>
                  <TableCell>{t('admin.circles.started')}</TableCell>
                  <TableCell>{t('admin.common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {circles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        {t('admin.circles.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  circles.map((circle: AdminCircle) => {
                    const circleActive = isCircleActive(circle);

                    return (
                      <TableRow key={circle.id}>
                        <TableCell>
                          <Chip
                            size="small"
                            label={circleActive ? t('admin.common.active') : t('admin.common.inactive')}
                            color={circleActive ? 'success' : 'default'}
                            variant={circleActive ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Stack spacing={0.5}>
                            <span>{pairLabel(circle as PairLabelSource)}</span>
                            {circle.warmup_rule_id && (
                              <Chip size="small" label={t('admin.warmups.sourceLabel')} variant="outlined" sx={{ width: 'fit-content' }} />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{circle.signal_spread_percent}%</TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography variant="body2">{circle.negative_spread_percent ?? DEFAULT_NEGATIVE_SPREAD_PERCENT}%</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {circle.cooldown_hours}h
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {(circle.sent_notifications ?? 0) + (circle.failed_notifications ?? 0)} / {circle.transaction_count ?? 0}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography variant="body2">{formatDateTime(circle.started_at)}</Typography>
                            {circle.expires_at && (
                              <Typography variant="caption" color="text.secondary">
                                {t('admin.circles.expires')}: {formatDateTime(circle.expires_at)}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<StopIcon />}
                            disabled={!circleActive || stopMutation.isPending}
                            onClick={() => stopMutation.mutate(circle.id)}
                          >
                            {t('admin.circles.stop')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <>
          {warmupsError && <Alert severity="error" sx={{ mb: 2 }}>{t('admin.warmups.loadError')}</Alert>}
          {saveWarmupMutation.error && <Alert severity="error" sx={{ mb: 2 }}>{t('admin.warmups.saveError')}</Alert>}

          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              border: '1px solid rgba(226, 255, 238, 0.12)',
              bgcolor: 'rgba(244, 255, 249, 0.045)',
            }}
          >
            <Stack spacing={1.5}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 1.25 }}>
                <TextField
                  size="small"
                  label={t('admin.warmups.name')}
                  value={warmupFormData.name}
                  onChange={(event) => setWarmupFormData({ ...warmupFormData, name: event.target.value })}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>{t('admin.common.status')}</InputLabel>
                  <Select
                    value={warmupFormData.status}
                    label={t('admin.common.status')}
                    onChange={(event) => setWarmupFormData({ ...warmupFormData, status: event.target.value as 'active' | 'paused' })}
                  >
                    <MenuItem value="active">{t('admin.common.active')}</MenuItem>
                    <MenuItem value="paused">{t('admin.common.paused')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>{t('admin.warmups.pairs')}</InputLabel>
                <Select
                  multiple
                  value={warmupFormData.pair_ids}
                  label={t('admin.warmups.pairs')}
                  disabled={warmupEligiblePairs.length === 0}
                  renderValue={(selected) => {
                    const selectedIds = selected as number[];
                    return selectedIds.length
                      ? t('admin.warmups.selectedPairs', { count: selectedIds.length })
                      : t('admin.warmups.noPairsSelected');
                  }}
                  onChange={(event) => {
                    const value = event.target.value as number[] | string;
                    const pairIds = typeof value === 'string'
                      ? value.split(',').map(Number)
                      : value.map(Number);
                    setWarmupFormData({
                      ...warmupFormData,
                      pair_ids: pairIds.filter((pairId) => warmupEligiblePairIds.has(pairId)),
                    });
                  }}
                >
                  {warmupEligiblePairs.map((pair) => (
                    <MenuItem key={pair.id} value={pair.id}>
                      <Checkbox checked={warmupFormData.pair_ids.includes(pair.id)} />
                      <ListItemText primary={pairLabel(pair)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="info" sx={{ py: 0.75 }}>
                {t('admin.warmups.manualOnlyNotice')}
              </Alert>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.25 }}>
                <TextField
                  size="small"
                  label={t('admin.warmups.intervalMin')}
                  inputProps={integerInputProps}
                  value={warmupFormData.interval_min_minutes}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedIntegerInput(value)) {
                      setWarmupFormData({ ...warmupFormData, interval_min_minutes: value });
                    }
                  }}
                />
                <TextField
                  size="small"
                  label={t('admin.warmups.intervalMax')}
                  inputProps={integerInputProps}
                  value={warmupFormData.interval_max_minutes}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedIntegerInput(value)) {
                      setWarmupFormData({ ...warmupFormData, interval_max_minutes: value });
                    }
                  }}
                />
                <TextField
                  size="small"
                  label={t('admin.warmups.durationMin')}
                  inputProps={integerInputProps}
                  value={warmupFormData.duration_min_minutes}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedIntegerInput(value)) {
                      setWarmupFormData({ ...warmupFormData, duration_min_minutes: value });
                    }
                  }}
                />
                <TextField
                  size="small"
                  label={t('admin.warmups.durationMax')}
                  inputProps={integerInputProps}
                  value={warmupFormData.duration_max_minutes}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedIntegerInput(value)) {
                      setWarmupFormData({ ...warmupFormData, duration_max_minutes: value });
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.25 }}>
                <TextField
                  size="small"
                  label={t('admin.warmups.spreadMin')}
                  inputProps={decimalInputProps}
                  value={warmupFormData.spread_min_percent}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedDecimalInput(value)) {
                      setWarmupFormData({ ...warmupFormData, spread_min_percent: value });
                    }
                  }}
                />
                <TextField
                  size="small"
                  label={t('admin.warmups.spreadMax')}
                  inputProps={decimalInputProps}
                  value={warmupFormData.spread_max_percent}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedDecimalInput(value)) {
                      setWarmupFormData({ ...warmupFormData, spread_max_percent: value });
                    }
                  }}
                />
                <TextField
                  size="small"
                  label={t('admin.warmups.negativeSpread')}
                  inputProps={decimalInputProps}
                  value={warmupFormData.negative_spread_percent}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedDecimalInput(value)) {
                      setWarmupFormData({ ...warmupFormData, negative_spread_percent: value });
                    }
                  }}
                />
                <TextField
                  size="small"
                  label={t('admin.warmups.cooldownHours')}
                  inputProps={integerInputProps}
                  value={warmupFormData.cooldown_hours}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (isAllowedIntegerInput(value)) {
                      setWarmupFormData({ ...warmupFormData, cooldown_hours: value });
                    }
                  }}
                />
              </Box>

              <TextField
                size="small"
                label={t('admin.circles.notes')}
                value={warmupFormData.notes}
                onChange={(event) => setWarmupFormData({ ...warmupFormData, notes: event.target.value })}
                multiline
                rows={2}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  {formatRange(warmupIntervalMin, warmupIntervalMax, 'm')} / {formatRange(warmupDurationMin, warmupDurationMax, 'm')} / {formatRange(warmupSpreadMin, warmupSpreadMax, '%')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  {warmupFormData.id && (
                    <Button
                      variant="outlined"
                      onClick={() => setWarmupFormData(createDefaultWarmupForm())}
                    >
                      {t('admin.warmups.cancelEdit')}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<LaunchIcon />}
                    onClick={() => saveWarmupMutation.mutate()}
                    disabled={!hasValidWarmupNumbers || saveWarmupMutation.isPending}
                  >
                    {warmupFormData.id ? t('admin.warmups.update') : t('admin.warmups.create')}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Box>

          <TableContainer component={Card} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.warmups.name')}</TableCell>
                  <TableCell>{t('admin.common.status')}</TableCell>
                  <TableCell>{t('admin.warmups.pairs')}</TableCell>
                  <TableCell>{t('admin.warmups.schedule')}</TableCell>
                  <TableCell>{t('admin.warmups.nextRun')}</TableCell>
                  <TableCell>{t('admin.warmups.runCount')}</TableCell>
                  <TableCell>{t('admin.common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {warmupRules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        {t('admin.warmups.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  warmupRules.map((rule) => {
                    const rulePairIds = getRulePairIds(rule);
                    const pairNames = rulePairIds
                      .map((pairId) => pairLookup.get(pairId))
                      .filter((pair): pair is ArbitragePair => Boolean(pair))
                      .map(pairLabel);

                    return (
                      <TableRow key={rule.id}>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Stack spacing={0.25}>
                            <Typography variant="body2" fontWeight={700}>{rule.name}</Typography>
                            {rule.last_error && (
                              <Typography variant="caption" color="error">
                                {rule.last_error}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={rule.status === 'active' ? t('admin.common.active') : t('admin.common.paused')}
                            color={rule.status === 'active' ? 'success' : 'default'}
                            variant={rule.status === 'active' ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Stack spacing={0.25}>
                            <Typography variant="body2">
                              {t('admin.warmups.selectedPairs', { count: rulePairIds.length })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {pairNames.slice(0, 2).join(', ') || '-'}
                              {pairNames.length > 2 ? ` +${pairNames.length - 2}` : ''}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Stack spacing={0.25}>
                            <Typography variant="body2">
                              {formatRange(rule.interval_min_minutes, rule.interval_max_minutes, 'm')} / {formatRange(rule.duration_min_minutes, rule.duration_max_minutes, 'm')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatRange(rule.spread_min_percent, rule.spread_max_percent, '%')} / {rule.cooldown_hours}h
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 170 }}>
                          <Stack spacing={0.25}>
                            <Typography variant="body2">{formatOptionalDate(rule.next_run_at)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('admin.warmups.lastRun')}: {formatOptionalDate(rule.last_run_at)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{rule.run_count}</TableCell>
                        <TableCell sx={{ minWidth: 300 }}>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PlayIcon />}
                              onClick={() => runWarmupNowMutation.mutate(rule.id)}
                              disabled={runWarmupNowMutation.isPending}
                            >
                              {t('admin.warmups.runNow')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={rule.status === 'active' ? <PauseIcon /> : <PlayIcon />}
                              onClick={() => updateWarmupStatusMutation.mutate({
                                id: rule.id,
                                status: rule.status === 'active' ? 'paused' : 'active',
                              })}
                              disabled={updateWarmupStatusMutation.isPending}
                            >
                              {rule.status === 'active' ? t('admin.warmups.pause') : t('admin.warmups.resume')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => editWarmupRule(rule)}
                            >
                              {t('admin.common.edit')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => deleteWarmupRule(rule)}
                              disabled={deleteWarmupMutation.isPending}
                            >
                              {t('admin.common.delete')}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            {t('admin.warmups.recentRuns')}
          </Typography>
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.warmups.rule')}</TableCell>
                  <TableCell>{t('admin.common.status')}</TableCell>
                  <TableCell>{t('admin.circles.pair')}</TableCell>
                  <TableCell>{t('admin.circles.signalSpreadShort')}</TableCell>
                  <TableCell>{t('admin.warmups.duration')}</TableCell>
                  <TableCell>{t('admin.circles.started')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {warmupRuns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('admin.warmups.noRuns')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  warmupRuns.map((run: WarmupSignalRun) => {
                    const runRule = warmupRules.find((rule) => rule.id === run.rule_id);
                    return (
                      <TableRow key={run.id}>
                        <TableCell>{runRule?.name ?? `#${run.rule_id}`}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={run.status}
                            color={run.status === 'launched' ? 'success' : run.status === 'error' ? 'error' : 'default'}
                            variant={run.status === 'launched' ? 'filled' : 'outlined'}
                          />
                          {run.error && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              {run.error}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>{run.pair_id ? pairLabel(run) : '-'}</TableCell>
                        <TableCell>{run.signal_spread_percent === null ? '-' : `${run.signal_spread_percent}%`}</TableCell>
                        <TableCell>{run.duration_minutes === null ? '-' : `${run.duration_minutes}m`}</TableCell>
                        <TableCell>{formatDateTime(run.started_at)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
